/**
 * Inbox thread-grouping logic — ported from frontend/src/app/inbox/page.tsx
 */

export interface Message {
  id: string
  user_id: string
  provider_id: string | null
  provider_name: string | null
  quote_request_id: string | null
  subject: string
  body: string
  message_type: string
  direction: 'inbound' | 'outbound'
  status: 'unread' | 'read' | 'archived' | 'deleted'
  attachments: any[]
  parsed_data: any
  received_at: string
  body_html?: string
  read_at: string | null
  created_at: string
  from_email?: string
  to_email?: string
  original_email_id?: string | null
  _bodyLoaded?: boolean
}

export interface InboxThread {
  threadKey: string
  providerName: string
  providerId: string | null
  subject: string
  messages: Message[]
  latestMessage: Message
  unreadCount: number
  hasUnread: boolean
}

export function stripSubjectPrefix(subject: string): string {
  return subject.replace(/^(Re:|Sv:|Fwd:|VS:|FW:)\s*/gi, '').trim()
}

export function filterForInbox(rawMessages: Message[]): Message[] {
  return rawMessages.filter(m => {
    if (m.parsed_data?.classification === 'auto_acknowledgment') return false
    if (m.message_type === 'quote' && m.direction === 'outbound') return false
    if (m.direction === 'outbound') {
      if (m.parsed_data?.email_type === 'fuldmagt') return true
      if (m.parsed_data?.type === 'switch_request_sent') return true
      if (m.message_type === 'reminder') return false
      if (m.quote_request_id && m.message_type === 'system') return false
    }
    return true
  })
}

export function groupIntoThreads(messages: Message[]): InboxThread[] {
  const threadMap = new Map<string, Message[]>()

  for (const msg of messages) {
    const provider = msg.provider_name || (msg.message_type === 'system' ? 'ForbrugerAgenten' : 'Ukendt')

    // Group ALL messages from the same provider into one thread
    const threadKey = provider

    if (!threadMap.has(threadKey)) threadMap.set(threadKey, [])
    threadMap.get(threadKey)!.push(msg)
  }

  const threads: InboxThread[] = Array.from(threadMap.entries()).map(([key, msgs]) => {
    const sorted = msgs.sort((a, b) => new Date(a.received_at).getTime() - new Date(b.received_at).getTime())
    const latest = sorted[sorted.length - 1]
    const unreadCount = sorted.filter(m => m.status === 'unread' && m.direction === 'inbound').length
    // Show the latest message's subject as thread subject (since all provider emails are now in one thread)
    const displaySubject = stripSubjectPrefix(latest.subject) || latest.subject

    return {
      threadKey: key,
      providerName: latest.provider_name || (latest.message_type === 'system' ? 'ForbrugerAgenten' : 'Ukendt'),
      providerId: latest.provider_id || sorted[0]?.provider_id || null,
      subject: displaySubject,
      messages: sorted,
      latestMessage: latest,
      unreadCount,
      hasUnread: unreadCount > 0,
    }
  })

  threads.sort((a, b) => {
    return new Date(b.latestMessage.received_at).getTime() - new Date(a.latestMessage.received_at).getTime()
  })

  return threads
}

/**
 * Returns true when a thread has an unanswered outbound message —
 * i.e. the latest outbound has no newer inbound reply.
 */
export function isAwaitingReply(thread: InboxThread): boolean {
  if (thread.providerName === 'Ukendt' || thread.providerId === null) return false
  const latestOutbound = [...thread.messages].reverse().find(m => m.direction === 'outbound')
  if (!latestOutbound) return false
  const outboundTime = new Date(latestOutbound.received_at).getTime()
  return !thread.messages.some(m =>
    m.direction === 'inbound' && new Date(m.received_at).getTime() > outboundTime
  )
}

/**
 * Returns true when a thread has an inbound message that requires user action
 * and hasn't been handled (no response, no auto-reply).
 * Uses GPT's `requires_user_action` field + system's `info_request` escalation.
 */
export function needsAction(thread: InboxThread): boolean {
  if (thread.providerName === 'Ukendt' || thread.providerId === null) return false
  return thread.messages.some(m => {
    if (m.direction !== 'inbound') return false
    const needsInput = m.parsed_data?.requires_user_action === true ||
                       m.parsed_data?.action_type === 'info_request'
    if (!needsInput) return false
    if (m.parsed_data?.responded) return false
    // Check if system auto-replied after this message
    const msgTime = new Date(m.received_at).getTime()
    return !thread.messages.some(r =>
      r.direction === 'outbound' && new Date(r.received_at).getTime() > msgTime
    )
  })
}

/**
 * Compute counts for all inbox filter tabs from a list of threads.
 * Used by inbox.tsx tab UI and by tests to verify seed data fullness.
 */
export function computeTabCounts(threads: InboxThread[]): {
  modtaget: number
  afventer: number
  regninger: number
  varsler: number
  fuldmagter: number
  sendt: number
  alle: number
} {
  return {
    modtaget: threads.filter(needsAction).length,
    afventer: threads.filter(isAwaitingReply).length,
    regninger: threads.filter(t =>
      t.messages.some(m =>
        m.message_type === 'bill' ||
        m.parsed_data?.classification === 'bill'
      )
    ).length,
    varsler: threads.filter(t =>
      t.messages.some(m => m.parsed_data?.classification === 'notice')
    ).length,
    fuldmagter: threads.filter(t =>
      t.messages.some(m =>
        m.parsed_data?.email_type === 'fuldmagt' ||
        m.parsed_data?.classification === 'fuldmagt_confirmation' ||
        m.subject?.toLowerCase().includes('fuldmagt')
      )
    ).length,
    sendt: threads.filter(t => t.messages.some(m => m.direction === 'outbound')).length,
    alle: threads.length,
  }
}

export function getMessagePreview(msg: Message): string {
  if (msg.body) {
    const text = msg.body.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
    return text.length > 90 ? text.slice(0, 90) + '...' : text
  }
  return msg.subject || ''
}

export function formatShortDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const todayStr = now.toDateString()
  if (date.toDateString() === todayStr) return date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })
  return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
}

export function getThreadBadge(thread: InboxThread): { label: string; color: string; bg: string } | null {
  const badges = getThreadBadges(thread)
  return badges.length > 0 ? badges[0] : null
}

export function getThreadBadges(thread: InboxThread): { label: string; color: string; bg: string }[] {
  const msgs = thread.messages
  const badges: { label: string; color: string; bg: string }[] = []

  if (msgs.some(m =>
    m.parsed_data?.email_type === 'fuldmagt' ||
    m.parsed_data?.classification === 'fuldmagt_confirmation' ||
    m.subject?.toLowerCase().includes('fuldmagt')
  )) badges.push({ label: 'Fuldmagt', color: '#059669', bg: '#ecfdf5' })

  if (msgs.some(m => m.message_type === 'bill' || m.parsed_data?.classification === 'bill'))
    badges.push({ label: 'Regning', color: '#c2410c', bg: '#fff7ed' })

  if (msgs.some(m => m.parsed_data?.classification === 'notice'))
    badges.push({ label: 'Varsel', color: '#dc2626', bg: '#fef2f2' })

  return badges
}

export { getProviderColor } from '../components/QuoteCard'
